/**
 * Variant Selection Validation
 *
 * When the option is disabled and variants are not all selected/clicked at least once:
 * - Replace styles for ":checked+.label-swatch:after" with border styles
 * - Prevent add to cart and scroll to VariantPicker
 * - Add error style to product-form__input for 3 seconds
 *
 * Once all variants are selected/clicked, restore default behavior
 */

(function () {
  "use strict";

  // Debug mode - set to false in production for better performance
  const DEBUG_MODE = false;
  const log = DEBUG_MODE ? console.log.bind(console) : () => {};

  // Store initialized containers to avoid duplicate initialization
  const initializedContainers = new WeakSet();

  // Re-fire bundle:change after every variant change so ProductBuyPrice recalculates
  // the correct total (variant price + any currently checked bundle prices).
  // After a variant change, theme.js calls updateSourceFromDestination('StickyPrice') which
  // replaces the product-buy-price element with a fresh one whose data-price reflects the
  // selected variant. Dispatching bundle:change triggers onBundleChanged:
  //   subtotal = data-price (selected variant) + sum(currently checked bundles)
  // This runs AFTER updateSourceFromDestination because variant:change is dispatched after it.
  //
  // IMPORTANT: theme.js also calls updateSourceFromDestination('ProductBundle') which
  // re-injects server-rendered bundle checkbox HTML — potentially re-checking bundles
  // that the user had explicitly unchecked. We track the user's explicit unchecking
  // actions and restore their intent after every variant:change.
  function initStickyPriceFix() {
    // Map<sectionEl, Set<bundleVariantId>> — tracks which bundles the user has explicitly
    // unchecked per section. Scoped per section to avoid cross-product pollution.
    var userUncheckedBundlesBySection = new WeakMap();

    // Listen globally to bundle checkbox changes to track user intent.
    // Must be registered before variant:change so it captures unchecks that happen
    // before the first variant click.
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'checkbox' || input.name !== 'bundles') return;
      var section = input.closest('.shopify-section');
      if (!section) return;
      if (!userUncheckedBundlesBySection.has(section)) {
        userUncheckedBundlesBySection.set(section, new Set());
      }
      var unchecked = userUncheckedBundlesBySection.get(section);
      if (input.checked) {
        // User re-checked — clear the "unchecked" record for this variant.
        unchecked.delete(input.value);
      } else {
        // User unchecked — record this variant so we can restore it after AJAX.
        unchecked.add(input.value);
      }
    });

    document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function (form) {
      if (form.hasAttribute('data-sticky-price-fix')) return;
      form.setAttribute('data-sticky-price-fix', 'true');
      form.addEventListener('variant:change', function (e) {
        if (!e.detail || !e.detail.variant) return;
        var section = form.closest('.shopify-section');
        if (!section) return;

        // After AJAX, updateSourceFromDestination('ProductBundle') has re-injected
        // server-rendered bundle HTML which may have re-checked bundles the user unchecked.
        // Restore the user's intent: physically un-check those inputs in the DOM.
        var unchecked = userUncheckedBundlesBySection.get(section);
        if (unchecked && unchecked.size > 0) {
          unchecked.forEach(function (value) {
            // Use CSS.escape to safely build an attribute selector.
            var input = section.querySelector('input[name="bundles"][value="' + value.replace(/"/g, '\\"') + '"]');
            if (input && input.checked) {
              input.checked = false;
            }
          });
        }

        // Now query only bundles that are truly checked (after restoring user intent).
        var checkedBundles = Array.from(section.querySelectorAll('input[name="bundles"]:checked'));

        // Only re-dispatch bundle:change when bundles are actually checked.
        // If no bundles are checked, updateSourceFromDestination('StickyPrice') has already
        // injected the full Liquid-rendered price — dispatching here would overwrite it
        // with a plain money string via onBundleChanged.
        if (checkedBundles.length === 0) return;
        form.dispatchEvent(new CustomEvent('bundle:change', {
          detail: {
            items: checkedBundles.map(function (input) {
              return {
                id: input.value,
                quantity: 1,
                price: input.getAttribute('data-price')
              };
            })
          }
        }));
      });
    });
  }

  // Wait for DOM and theme.settings to be ready
  function waitForTheme() {
    if (typeof theme !== "undefined" && theme.settings) {
      // Always fix sticky price after variant change, regardless of validation setting
      initStickyPriceFix();

      // Get setting from theme (default is true, so we check if it's false)
      // If validation is enabled (default) or undefined, do nothing
      // If validation is disabled (false), we activate the modifications
      const settingValue = theme.settings.enable_variant_selection_validation;

      // If setting is undefined or true, validation is enabled (default behavior)
      // Only proceed if setting is explicitly false
      if (settingValue !== false) {
        return;
      }

      // Always initialize to set up quickview listeners,
      // even on pages without a product form at load time
      initAll();
    } else {
      // Retry immediately without delay (use requestAnimationFrame for next frame)
      requestAnimationFrame(waitForTheme);
    }
  }

  // Initialize as soon as possible
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", waitForTheme);
  } else {
    // DOM already loaded, start immediately
    waitForTheme();
  }

  // Initialize all product sections
  function initAll() {
    log("[Variant Validation] Initializing all product forms...");

    // Find all product forms on the page
    const productForms = document.querySelectorAll(
      '[data-type="add-to-cart-form"]'
    );

    log(`[Variant Validation] Found ${productForms.length} product form(s)`);

    productForms.forEach((productForm, index) => {
      log(`[Variant Validation] Processing form ${index + 1}`);

      // Skip if already processed
      if (productForm.hasAttribute("data-validation-processed")) {
        log(
          `[Variant Validation] Form ${index + 1} already processed, skipping`
        );
        return;
      }
      productForm.setAttribute("data-validation-processed", "true");

      // Find the closest section container (most specific first)
      let container = productForm.closest("quick-view");
      if (!container) container = productForm.closest(".featured-product");
      if (!container) container = productForm.closest(".shopify-section");
      if (!container) container = productForm.closest(".product");

      log(
        `[Variant Validation] Form ${index + 1} container:`,
        container ? container.className : "none"
      );

      if (container && !container.hasAttribute("data-validation-initialized")) {
        log(`[Variant Validation] Initializing form ${index + 1}`);
        initVariantValidation(container);
      } else if (container) {
        log(
          `[Variant Validation] Form ${index + 1} container already initialized`
        );
      }
    });

    // Store observers per modal to clean them up
    const modalObservers = new WeakMap();

    // Function to completely clean up a modal's state
    function cleanupModal(quickView) {
      if (!quickView) return;

      log("[Variant Validation] Cleaning up modal state");

      // Remove all validation attributes
      quickView.removeAttribute("data-validation-initialized");
      quickView.removeAttribute("data-validation-active");
      quickView.removeAttribute("data-validation-container");
      quickView.removeAttribute("data-variant-selection-complete");
      quickView.removeAttribute("data-user-has-interacted");

      // Remove from initialized containers
      initializedContainers.delete(quickView);

      // Find and remove all style elements for this specific modal
      const containerId =
        quickView.id || quickView.getAttribute("data-validation-container");
      if (containerId) {
        const styleElements = document.head.querySelectorAll(
          `style[data-validation-style*="${containerId}"]`
        );
        styleElements.forEach((el) => el.remove());
      }

      // Also remove any style elements that might be associated with quick-view
      const allStyles = document.head.querySelectorAll(
        "style[data-validation-style]"
      );
      allStyles.forEach((style) => {
        if (style.textContent.includes("quick-view")) {
          style.remove();
        }
      });

      // Remove processed attribute from form
      const productForm = quickView.querySelector(
        '[data-type="add-to-cart-form"]'
      );
      if (productForm) {
        productForm.removeAttribute("data-validation-processed");
      }

      // Stop and remove observer if exists
      const observer = modalObservers.get(quickView);
      if (observer) {
        observer.disconnect();
        modalObservers.delete(quickView);
      }
    }

    // Persistent MutationObserver: fires synchronously before the browser paints,
    // so modifications are applied before the user ever sees the raw content.
    // No events, no timeouts, no rAF — works on any device speed.
    const tryInitQuickView = (quickView) => {
      if (!quickView || !quickView.hasAttribute("open")) return;
      if (quickView.hasAttribute("data-validation-initialized")) return;

      const productForm = quickView.querySelector(
        '[data-type="add-to-cart-form"]'
      );
      const variantPicker = quickView.querySelector(
        'variant-picker[id^="VariantPicker"]'
      );

      if (!productForm || !variantPicker) return;

      log("[Variant Validation] Quick-view content ready, initializing");
      cleanupModal(quickView);
      productForm.removeAttribute("data-validation-processed");
      initVariantValidation(quickView);
    };

    const quickViewObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // open attribute added to an existing quick-view element
        if (mutation.type === "attributes") {
          if (
            mutation.target instanceof HTMLElement &&
            mutation.target.tagName === "QUICK-VIEW"
          ) {
            tryInitQuickView(mutation.target);
          }
          continue;
        }

        // Child nodes added — find the closest open quick-view
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          // Node added directly inside quick-view[open]
          const parent = node.parentElement;
          if (parent?.tagName === "QUICK-VIEW" && parent.hasAttribute("open")) {
            tryInitQuickView(parent);
            continue;
          }

          // Node is a quick-view element itself, already open
          if (node.tagName === "QUICK-VIEW" && node.hasAttribute("open")) {
            tryInitQuickView(node);
            continue;
          }

          // Node is nested deeper inside an open quick-view
          const quickView = node.closest?.("quick-view[open]");
          if (quickView) tryInitQuickView(quickView);
        }
      }
    });

    quickViewObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open"],
    });

    // Listen for modal/quick-view closings
    document.addEventListener("modal:afterHide", function (e) {
      const quickView = e.target;
      if (quickView && quickView.tagName === "QUICK-VIEW") {
        log("[Variant Validation] Modal closed, cleaning up");
        cleanupModal(quickView);
      }
    });

    // Also listen for when modal is removed from DOM
    const modalCloseObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.tagName === "QUICK-VIEW") {
            log("[Variant Validation] Modal removed from DOM, cleaning up");
            cleanupModal(node);
          }
        });
      });
    });

    // Observe document body for removed modals
    if (document.body) {
      modalCloseObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  function initVariantValidation(container) {
    log(
      "[Variant Validation] initVariantValidation called for:",
      container.className || container.tagName
    );

    // Check if already initialized (but allow reinit for modals)
    const isModal = container.tagName === "QUICK-VIEW";

    // For modals, always force reinitialization to ensure fresh state
    if (isModal && container.hasAttribute("data-validation-initialized")) {
      log("[Variant Validation] Modal already initialized, forcing reinit");
      // Remove old initialization markers
      container.removeAttribute("data-validation-initialized");
      container.removeAttribute("data-validation-active");
      container.removeAttribute("data-user-has-interacted");
      container.removeAttribute("data-variant-selection-complete");

      // Remove old style elements for this specific modal
      const oldContainerId = container.getAttribute(
        "data-validation-container"
      );
      if (oldContainerId) {
        const oldStyles = document.head.querySelectorAll(
          `style[data-validation-style*="${oldContainerId}"]`
        );
        oldStyles.forEach((el) => el.remove());
      }
    } else if (
      container.hasAttribute("data-validation-initialized") &&
      !isModal
    ) {
      log("[Variant Validation] Already initialized, skipping");
      return;
    }

    // Mark as initialized
    container.setAttribute("data-validation-initialized", "true");

    // Always generate a new unique identifier for modals to ensure isolation
    // For other containers, reuse if exists
    if (isModal || !container.hasAttribute("data-validation-container")) {
      const uniqueId = `validation-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      container.setAttribute("data-validation-container", uniqueId);
      log(`[Variant Validation] Generated unique container ID: ${uniqueId}`);
    }

    // Find product form within the container
    const productForm = container.querySelector(
      '[data-type="add-to-cart-form"]'
    );
    if (!productForm) {
      log("[Variant Validation] No product form found, skipping");
      return;
    }

    // Find variant picker within the container
    const variantPicker = container.querySelector(
      'variant-picker[id^="VariantPicker"]'
    );
    if (!variantPicker) {
      log("[Variant Validation] No variant picker found, skipping");
      return;
    }

    log("[Variant Validation] Product form and variant picker found");

    // Track clicked variants (one per option group)
    const clickedVariants = new Set();
    const variantInputs = variantPicker.querySelectorAll('input[type="radio"]');

    // Track if validation is complete (all variants clicked at least once)
    let validationComplete = false;
    const tieredBundleActive = !!window.__tieredBundle;

    function markVariantSelectionComplete() {
      validationComplete = true;
      container.setAttribute("data-variant-selection-complete", "true");
      container.dispatchEvent(
        new CustomEvent("variant-validation:complete", { bubbles: true })
      );
    }

    // Track if the user has interacted with a variant option at least once
    let userHasInteracted = false;

    // Get all option groups (fieldsets)
    const optionGroups = Array.from(
      variantPicker.querySelectorAll("fieldset.product-form__input")
    );
    const totalOptions = optionGroups.length;

    log(`[Variant Validation] Found ${totalOptions} option group(s)`);

    // If product has no options or only default variant, skip custom behavior
    if (totalOptions === 0) {
      log("[Variant Validation] No variant options found, skipping validation");
      return;
    }

    log("[Variant Validation] Proceeding with validation setup...");

    // Get the form ID
    const formId = productForm.getAttribute("id");

    // Find add to cart buttons with form attribute
    // Look for buttons with form attribute matching this form
    const addToCartButtons = Array.from(
      document.querySelectorAll(
        `button[type="submit"][form="${formId}"], button[form="${formId}"][type="submit"]`
      )
    );

    // Find price elements within the container
    const priceElements = Array.from(
      container.querySelectorAll('[id^="BuyButtonPrice-"]')
    );

    // Find badges container within the container
    const badgeContainers = Array.from(
      container.querySelectorAll(".product__badges .badges, .badges")
    );

    log(
      `[Variant Validation] Found ${badgeContainers.length} badge container(s)`
    );

    // Find price__regular elements within the container
    // Exclude elements inside bundle products ([data-bundle-price]) and complementary product carousels
    const priceRegularElements = Array.from(
      container.querySelectorAll(".price__regular")
    ).filter(
      (el) =>
        !el.closest("[data-bundle-price]") &&
        !el.closest("product-recommendations")
    );

    log(
      `[Variant Validation] Found ${priceRegularElements.length} price__regular element(s)`
    );

    // Find price__sale elements (struck-through prices) scoped to main product only
    const priceSaleElements = Array.from(
      container.querySelectorAll(".price__sale")
    ).filter(
      (el) =>
        !el.closest("[data-bundle-price]") &&
        !el.closest("product-recommendations")
    );

    // Store original price texts
    const originalPriceTexts = new Map();
    priceRegularElements.forEach((priceEl) => {
      originalPriceTexts.set(priceEl, priceEl.innerHTML);
    });

    // Store original display states of price__sale elements
    const originalPriceSaleDisplays = new Map();

    // Aperçu promo « même prix final » : sauvegarde du HTML complet des blocs .price
    const originalPriceContainerHTML = new Map();

    // Store original regular price colors
    const originalRegularColors = new Map();


    // Store original badge texts
    const originalBadgeTexts = new Map();

    // Track badges created by this script
    const createdBadges = new Set();

    // Function to get product data from container
    function getProductData() {
      // First, try to read from container-specific script tag
      const dataScript = container.querySelector(
        "script[data-product-variant-data]"
      );
      if (dataScript) {
        try {
          const data = JSON.parse(dataScript.textContent);
          log("[Variant Validation] Using container-specific product data");
          return data;
        } catch (e) {
          log(
            "[Variant Validation] Failed to parse container product data:",
            e
          );
        }
      }

      // Fallback to global theme object
      if (typeof theme !== "undefined") {
        log("[Variant Validation] Using global theme product data");
        return {
          variantSelectText: theme.variantSelectText,
          variantSelectButtonText: theme.variantSelectButtonText,
          variantOptionMeta: theme.variantOptionMeta,
          productVariants: theme.productVariants,
          productPriceMin: theme.productPriceMin,
          productPriceMax: theme.productPriceMax,
          productPriceVaries: theme.productPriceVaries,
        };
      }

      log("[Variant Validation] No product data found");
      return {};
    }

    const productData = getProductData();

    let productUseUpToRange = false;
    let productKeepNativeSalePrice = false;

    // Sticky options/price: only on main product page (not quickview/modal)
    // Scope to the parent .shopify-section so that a featured-product section (which has no
    // product-sticky-form of its own) cannot accidentally modify the main product's sticky block.
    const sectionEl = !isModal ? (container.closest('.shopify-section') || container) : null;
    const stickyProductOptions = sectionEl ? sectionEl.querySelector('[data-sticky-product-options]') : null;
    const stickyOriginalText = stickyProductOptions ? stickyProductOptions.innerText.trim() : null;
    const stickyBuyPrice = sectionEl ? sectionEl.querySelector('product-sticky-form product-buy-price') : null;
    const stickyOriginalPriceHTML = stickyBuyPrice ? stickyBuyPrice.innerHTML : null;
    let stickyOptionsReverted = false;

    // Normalize option meta entries from metaobject
    function handleize(str) {
      return (str || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    function normalizeOptionMeta(meta) {
      if (!meta) return [];
      if (Array.isArray(meta)) return meta;
      return [meta];
    }

    const optionMetaEntries = normalizeOptionMeta(
      productData.variantOptionMeta || null
    );

    const optionMetaByHandle = new Map();
    optionMetaEntries.forEach((entry) => {
      if (entry && entry.option_name) {
        optionMetaByHandle.set(handleize(entry.option_name), entry);
      }
    });

    // Store original button texts
    const originalButtonTexts = new Map();
    addToCartButtons.forEach((button) => {
      // Try different selectors to find the text span
      let textSpan = button.querySelector(".btn-text > span:first-child");
      if (!textSpan) {
        textSpan = button.querySelector(".btn-text > span.hidden.md\\:block");
      }
      if (!textSpan) {
        textSpan = button.querySelector(".btn-text span");
      }
      if (textSpan) {
        originalButtonTexts.set(button, textSpan.textContent.trim());
      }
    });

    // Get metafield text or translation
    function getVariantSelectText() {
      if (
        productData.variantSelectText &&
        productData.variantSelectText.metafield &&
        productData.variantSelectText.metafield.trim() !== ""
      ) {
        return productData.variantSelectText.metafield;
      }
      // Fallback to translation
      if (
        productData.variantSelectText &&
        productData.variantSelectText.translation
      ) {
        return productData.variantSelectText.translation;
      }
      // Final fallback
      return "Select size";
    }

    // Get button text from metaobject (variant selection)
    function getMetaButtonText() {
      for (const entry of optionMetaEntries) {
        if (
          entry &&
          typeof entry.variant_selection_button_text === "string" &&
          entry.variant_selection_button_text.trim() !== ""
        ) {
          return entry.variant_selection_button_text;
        }
      }
      return null;
    }

    // Get button text from metafield or translation
    function getButtonText() {
      const metaButton = getMetaButtonText();
      if (metaButton) return metaButton;
      if (
        productData.variantSelectButtonText &&
        productData.variantSelectButtonText.metafield &&
        productData.variantSelectButtonText.metafield.trim() !== ""
      ) {
        return productData.variantSelectButtonText.metafield;
      }
      // Fallback to translation
      if (
        productData.variantSelectButtonText &&
        productData.variantSelectButtonText.translation
      ) {
        return productData.variantSelectButtonText.translation;
      }
      // Final fallback
      return "Select size";
    }

    // Function to get current label text divs (re-select in case DOM was updated)
    function getLabelTextDivs() {
      // Get all divs with class "flex gap-2" that are direct children of form__label
      const allFlexGap2Divs = Array.from(
        variantPicker.querySelectorAll(".form__label > .flex.gap-2")
      );

      // Filter to get only the text divs (not the size chart button container)
      // The text div either has a span.font-medium OR contains the metafield text
      const textToUse = getVariantSelectText();
      return allFlexGap2Divs.filter((div) => {
        // Check if it has span.font-medium (original structure)
        const hasFontMedium = div.querySelector("span.font-medium") !== null;
        // Check if it contains the metafield text (after replacement)
        const containsMetafieldText =
          div.textContent.includes(textToUse) ||
          div.innerHTML.includes(textToUse);
        // Return true if either condition is met
        return hasFontMedium || containsMetafieldText;
      });
    }

    // Function to find meta entry for a given fieldset
    function findMetaForFieldset(fieldset) {
      if (!fieldset) return null;
      const slug = fieldset.dataset?.optionSlug;
      if (!slug) return null;
      return optionMetaByHandle.get(slug) || null;
    }

    // Find all label divs with "flex gap-2" class under form__label
    const labelTextDivs = getLabelTextDivs();

    // Store original text content for each label div
    const originalTexts = new Map();
    labelTextDivs.forEach((div) => {
      originalTexts.set(div, div.innerHTML);
    });

    // Generate unique ID for this instance using container ID
    // This ensures each modal/section has completely isolated styles
    const containerId =
      container.id ||
      container.getAttribute("data-validation-container") ||
      `container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const instanceId = `variant-validation-${containerId}`;

    // Store original styles
    let originalStyleSheet = null;
    let styleElement = null;

    // Get unique selector for this container
    function getContainerSelector() {
      if (container.id) {
        return `#${container.id}`;
      }
      const validationAttr = container.getAttribute(
        "data-validation-container"
      );
      if (validationAttr) {
        return `[data-validation-container="${validationAttr}"]`;
      }
      // Fallback: use a combination of attributes
      return `[data-validation-container]`;
    }

    // Function to apply modified styles with scoped selectors
    function applyModifiedStyles() {
      // Remove any existing style element for this instance to ensure fresh state
      const existingStyle = document.head.querySelector(
        `style[data-validation-style="${instanceId}"]`
      );
      if (existingStyle) {
        existingStyle.remove();
      }

      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }

      styleElement = document.createElement("style");
      styleElement.setAttribute("data-validation-style", instanceId);

      // Scope styles to this container only
      const containerSelector = getContainerSelector();

      // Determine swatch border color: red if the pre-selected variant is sold out,
      // default border color otherwise.
      let swatchBorderColor = "rgb(var(--color-border))";
      if (!userHasInteracted && productData.productVariants) {
        const checkedInputs = Array.from(
          variantPicker.querySelectorAll('input[type="radio"]:checked')
        );
        const selectedOptions = checkedInputs.map((inp) => inp.value);
        if (selectedOptions.length > 0) {
          const preselectedVariant = productData.productVariants.find(
            (v) =>
              v.options &&
              selectedOptions.every((opt, i) => v.options[i] === opt)
          );
          if (preselectedVariant && !preselectedVariant.available) {
            swatchBorderColor = "rgb(var(--color-red-200))";
          }
        }
      }

      styleElement.textContent = `
        ${containerSelector}[data-validation-active] :checked+.label-swatch::after,
        ${containerSelector}[data-validation-active] input[type="radio"]:checked+.label-swatch::after {
          --tw-shadow-colored: 0 0 0 1px var(--tw-shadow-color);
          --tw-shadow-color: ${swatchBorderColor};
          --tw-shadow: var(--tw-shadow-colored);
        }

        /* Keep hover functionality with colored border */
        @media screen and (pointer:fine) {
          ${containerSelector}[data-validation-active] .swatches input:is([type=checkbox], [type=radio]):not(:disabled, .disabled)+.label-swatch:hover::after {
            --tw-shadow-colored: 0 0 0 2px var(--tw-shadow-color);
            --tw-shadow-color: rgb(var(--color-foreground));
            --tw-shadow: var(--tw-shadow-colored);
            inset: 0;
          }
        }
      `;
      document.head.appendChild(styleElement);

      // Mark container as having validation active
      container.setAttribute("data-validation-active", "true");
    }

    // Function to remove modified styles
    function removeModifiedStyles() {
      // Remove the validation active marker
      container.removeAttribute("data-validation-active");

      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
        styleElement = null;
      }
    }

    // Function to check if all variants have been clicked
    function allVariantsClicked() {
      // Check if we have at least one clicked variant per option group
      const clickedPerGroup = new Set();
      clickedVariants.forEach((variantId) => {
        const input = variantPicker.querySelector(
          `input[type="radio"][id="${variantId}"]`
        );
        if (input) {
          const fieldset = input.closest("fieldset.product-form__input");
          if (fieldset) {
            clickedPerGroup.add(fieldset);
          }
        }
      });
      return clickedPerGroup.size >= totalOptions && totalOptions > 0;
    }

    let errorStyleTimeout = null;
    let labelErrorTimeout = null;

    // Function to clear error style immediately
    function removeErrorStyle() {
      if (errorStyleTimeout) {
        clearTimeout(errorStyleTimeout);
        errorStyleTimeout = null;
      }
      if (labelErrorTimeout) {
        clearTimeout(labelErrorTimeout);
        labelErrorTimeout = null;
      }
      optionGroups.forEach((fieldset) => {
        fieldset.style.border = "";
        fieldset.style.borderRadius = "";
        fieldset.style.padding = "";
      });
      clearRedColorFromText();
    }

    // Function to add error style to product-form__input
    function addErrorStyle() {
      // Ensure previous timeout is cleared so we can remove early
      if (errorStyleTimeout) {
        clearTimeout(errorStyleTimeout);
        errorStyleTimeout = null;
      }

      optionGroups.forEach((fieldset) => {
        fieldset.style.border = "1px solid rgb(var(--color-error-text))";
        fieldset.style.borderRadius = "var(--rounded-input)";
        fieldset.style.padding = "var(--sp-2)";
      });

      // Remove after 3 seconds (can be cleared earlier on user interaction)
      errorStyleTimeout = setTimeout(() => {
        removeErrorStyle();
      }, 3000);
    }

    // Scroll the variant picker into view without using scrollIntoView on the picker.
    // scrollIntoView() walks the full ancestor chain and can scroll the quick-view drawer
    // or the window; that breaks the sticky "read more" bar (layout / stacking vs hover).
    function scrollToVariantPicker() {
      const behavior = "smooth";

      function scrollElementToCenterChild(scrollEl, childEl) {
        if (!scrollEl || !childEl) return false;
        const style = window.getComputedStyle(scrollEl);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowY === "overlay") &&
          scrollEl.scrollHeight > scrollEl.clientHeight + 1;
        if (!canScrollY) return false;

        const parentRect = scrollEl.getBoundingClientRect();
        const childRect = childEl.getBoundingClientRect();
        const relativeTop =
          childRect.top - parentRect.top + scrollEl.scrollTop;
        const target =
          relativeTop -
          scrollEl.clientHeight / 2 +
          childEl.offsetHeight / 2;
        const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
        scrollEl.scrollTo({
          top: Math.max(0, Math.min(target, maxScroll)),
          behavior,
        });
        return true;
      }

      if (scrollElementToCenterChild(variantPicker.closest(".product__info"), variantPicker)) {
        return;
      }

      const quickView = variantPicker.closest("quick-view");
      if (quickView) {
        const drawerContent =
          quickView.querySelector(".quick-view__content.drawer__content") ||
          quickView.querySelector(".drawer__content");
        if (scrollElementToCenterChild(drawerContent, variantPicker)) {
          return;
        }
      }

      variantPicker.scrollIntoView({ behavior, block: "center" });
    }

    // Function to replace label text with metaobject/translation text
    function replaceLabelTexts() {
      const currentDivs = getLabelTextDivs();
      currentDivs.forEach((div) => {
        const fieldset = div.closest("fieldset.product-form__input");
        const meta = findMetaForFieldset(fieldset);
        const textToUse =
          (meta &&
            typeof meta.selection === "string" &&
            meta.selection.trim() !== "" &&
            meta.selection) ||
          getVariantSelectText();
        div.innerHTML = textToUse;
      });
    }

    // Function to restore original label texts
    function restoreLabelTexts() {
      const currentDivs = getLabelTextDivs();
      currentDivs.forEach((div) => {
        const originalText = originalTexts.get(div);
        if (originalText) {
          div.innerHTML = originalText;
        }
      });
    }

    // Function to replace button text with metafield text
    function replaceButtonText() {
      const buttonText = getButtonText();
      if (!buttonText) {
        console.warn("No button text found (metafield or translation)");
        return;
      }

      if (addToCartButtons.length === 0) {
        console.warn("No add to cart buttons found");
        return;
      }

      addToCartButtons.forEach((button) => {
        // Try different selectors to find the text span
        // First try the direct child span (most common case)
        let textSpan = button.querySelector(".btn-text > span:first-child");
        if (!textSpan) {
          // Try the hidden md:block span
          textSpan = button.querySelector(".btn-text > span.hidden.md\\:block");
        }
        if (!textSpan) {
          // Try any span inside btn-text
          textSpan = button.querySelector(".btn-text span");
        }
        if (textSpan) {
          // Store original if not already stored
          if (!originalButtonTexts.has(button)) {
            originalButtonTexts.set(button, textSpan.textContent.trim());
          }
          textSpan.textContent = buttonText;
        } else {
          // If no span found, try to set text directly on btn-text
          const btnText = button.querySelector(".btn-text");
          if (btnText) {
            // Store original if not already stored
            if (!originalButtonTexts.has(button)) {
              originalButtonTexts.set(button, btnText.textContent.trim());
            }
            // Replace the first text node or create a new span
            const firstSpan = btnText.querySelector("span:first-child");
            if (firstSpan) {
              firstSpan.textContent = buttonText;
            } else {
              // Create a new span if none exists
              const newSpan = document.createElement("span");
              newSpan.textContent = buttonText;
              btnText.insertBefore(newSpan, btnText.firstChild);
            }
          }
        }
      });
    }

    // Function to restore original button texts
    function restoreButtonTexts() {
      addToCartButtons.forEach((button) => {
        const originalText = originalButtonTexts.get(button);
        if (originalText) {
          // Try different selectors to find the text span
          let textSpan = button.querySelector(".btn-text > span:first-child");
          if (!textSpan) {
            textSpan = button.querySelector(
              ".btn-text > span.hidden.md\\:block"
            );
          }
          if (!textSpan) {
            textSpan = button.querySelector(".btn-text span");
          }
          if (textSpan) {
            textSpan.textContent = originalText;
          }
        }
      });
    }

    // Function to hide price elements
    function hidePriceElements() {
      priceElements.forEach((priceEl) => {
        priceEl.style.display = "none";
      });
    }

    // Function to show price elements
    function showPriceElements() {
      priceElements.forEach((priceEl) => {
        priceEl.style.display = "";
      });
    }

    // Function to remove color from price__regular elements
    function removePriceRegularColor() {
      priceRegularElements.forEach((priceEl) => {
        if (!originalRegularColors.has(priceEl)) {
          originalRegularColors.set(priceEl, priceEl.style.color);
        }
        priceEl.style.color = "inherit";
      });
    }

    // Function to restore color on price__regular elements
    function restorePriceRegularColor() {
      priceRegularElements.forEach((priceEl) => {
        const originalColor = originalRegularColors.get(priceEl);
        priceEl.style.color = originalColor || "";
      });
    }

    // Get badge meta from container or theme
    function getBadgeMeta(container) {
      const data = (container && container.dataset) || {};
      log("[Variant Validation] Badge container dataset:", data);

      const saveType =
        data.badgeSaveType ||
        (typeof theme !== "undefined" &&
          theme.settings &&
          theme.settings.product_save_type) ||
        "percent";
      const upToTemplate =
        data.badgeUpToTemplate ||
        (typeof theme !== "undefined" &&
          theme.strings &&
          theme.strings.upToOffTemplate) ||
        "";
      const saveTemplate = data.badgeSaveTemplate || "";
      const saleLabel = data.badgeSaleLabel || "";

      log(
        `[Variant Validation] Badge meta: saveType=${saveType}, upToTemplate="${upToTemplate}", saleLabel="${saleLabel}"`
      );

      return { saveType, upToTemplate, saveTemplate, saleLabel };
    }

    function formatMoney(cents) {
      if (typeof theme !== "undefined" && theme.Currency) {
        return theme.Currency.formatMoney(
          cents,
          theme.settings.currencyCodeEnabled
            ? theme.settings.moneyWithCurrencyFormat
            : theme.settings.moneyFormat
        );
      }
      return `€${(cents / 100).toFixed(2)}`;
    }

    function getVariantsList() {
      if (
        productData.productVariants &&
        Array.isArray(productData.productVariants)
      ) {
        return productData.productVariants;
      }

      const variantPickerData = variantPicker.querySelector(
        "script[type='application/json']"
      );
      if (variantPickerData) {
        try {
          return JSON.parse(variantPickerData.textContent);
        } catch (e) {
          log("[Variant Validation] Failed to parse variant data:", e);
        }
      }

      return null;
    }

    // Promo avec prix finaux identiques (ex. 20 € / 20 € barré 22 €) : garder le rendu SAVE
    // → prix actuel affiché normalement avec prix barré, sans badge "jusqu'à"
    function hasSamePriceVariantSale(variants) {
      if (!variants || !Array.isArray(variants) || variants.length < 2) {
        return false;
      }

      const prices = variants.filter((v) => v.price > 0).map((v) => v.price);
      if (prices.length === 0) return false;
      if (Math.min(...prices) !== Math.max(...prices)) {
        return false;
      }

      return variants.some(
        (v) => v.compare_at_price && v.price && v.compare_at_price > v.price
      );
    }

    // « Jusqu'à » UNIQUEMENT si les prix finaux diffèrent entre variantes ET qu'au moins
    // une variante a un prix barré Shopify (compare_at_price > price).
    // → ex : variante S à 20 € + variante M à 26 €, S a une réduction → « jusqu'à -X % »
    // Dans tous les autres cas (prix identiques, pas de promo) : badge natif ou pas de badge.
    function shouldUseUpToRange(variants) {
      if (!variants || !Array.isArray(variants) || variants.length < 2) {
        return false;
      }

      // Ne considérer que les variantes avec un prix > 0 pour la comparaison de fourchette
      const pricedVariants = variants.filter((v) => v.price > 0);
      if (pricedVariants.length < 2) return false;

      const prices = pricedVariants.map((v) => v.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      // Si tous les prix finaux sont identiques → pas de badge « jusqu'à »
      if (minPrice === maxPrice) {
        return false;
      }

      // Au moins une variante doit avoir un prix barré Shopify
      return variants.some(
        (v) => v.compare_at_price && v.price && v.compare_at_price > v.price
      );
    }

    // Données d'aperçu : prix final commun + prix barré max (variantes à promo)
    function getSamePriceSalePreviewData(variants) {
      if (!hasSamePriceVariantSale(variants)) return null;

      const pricedVariants = variants.filter((v) => v.price > 0);
      const minPrice = Math.min(...pricedVariants.map((v) => v.price));
      let maxCompareAt = 0;

      pricedVariants.forEach((v) => {
        if (
          v.price === minPrice &&
          v.compare_at_price > v.price &&
          v.compare_at_price > maxCompareAt
        ) {
          maxCompareAt = v.compare_at_price;
        }
      });

      if (maxCompareAt <= minPrice) return null;
      return { minPrice, maxCompareAt };
    }

    // Avant sélection : prix final + prix barré (comme le rendu promo natif)
    function applySamePriceSalePricePreview() {
      const preview = getSamePriceSalePreviewData(getVariantsList());
      if (!preview) return;

      const { minPrice, maxCompareAt } = preview;
      const regularFmt = formatMoney(minPrice);
      const saleFmt = formatMoney(maxCompareAt);

      container
        .querySelectorAll(".price[data-base-price]")
        .forEach((priceEl) => {
          if (
            priceEl.closest("[data-bundle-price]") ||
            priceEl.closest("product-recommendations")
          ) {
            return;
          }

          if (!originalPriceContainerHTML.has(priceEl)) {
            originalPriceContainerHTML.set(priceEl, priceEl.innerHTML);
          }

          priceEl.classList.add("price--on-sale");
          priceEl.dataset.basePrice = String(minPrice);
          priceEl.dataset.baseCompareAtPrice = String(maxCompareAt);

          const discountRange = priceEl.querySelector(".price__discount-range");
          if (discountRange) discountRange.style.display = "none";

          priceEl.innerHTML =
            `<span class="price__regular whitespace-nowrap">${regularFmt}</span>` +
            `<span class="price__sale inline-flex items-center h-auto relative">${saleFmt}</span>`;
        });
    }

    function restoreSamePriceSalePricePreview() {
      originalPriceContainerHTML.forEach((html, priceEl) => {
        if (priceEl.isConnected) {
          priceEl.innerHTML = html;
          const discountRange = priceEl.querySelector(".price__discount-range");
          if (discountRange) discountRange.style.display = "";
        }
      });
      originalPriceContainerHTML.clear();
    }

    // Badge promo classique (-X %), sans « jusqu'à »
    function applySamePriceSaleBadge() {
      if (!hasSamePriceVariantSale(getVariantsList())) return;

      const savings = getMaxSavings();
      if (!savings || badgeContainers.length === 0) return;

      badgeContainers.forEach((container) => {
        const { saveType, saveTemplate, saleLabel } = getBadgeMeta(container);

        let badges = Array.from(container.querySelectorAll(".badge--onsale"));

        if (badges.length === 0) {
          const newBadge = document.createElement("span");
          newBadge.className =
            "badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full";
          newBadge.dataset.variantValidationBadge = "true";
          container.appendChild(newBadge);
          badges = [newBadge];
          createdBadges.add(newBadge);
        }

        let textToApply = "";
        if (saveType === "label") {
          textToApply = saleLabel || "";
        } else if (saveType === "dollar") {
          if (!savings.amountFormatted) return;
          if (saveTemplate) {
            textToApply = saveTemplate
              .replace("__amount__", savings.amountFormatted)
              .replace("{{ saved_amount }}", savings.amountFormatted);
          } else {
            textToApply = savings.amountFormatted;
          }
        } else {
          if (!savings.percent) return;
          const percentText = `${savings.percent}%`;
          if (saveTemplate) {
            textToApply = saveTemplate
              .replace("__amount__", percentText)
              .replace("{{ saved_amount }}", percentText);
          } else {
            textToApply = percentText;
          }
        }

        badges.forEach((badge) => {
          if (!originalBadgeTexts.has(badge)) {
            originalBadgeTexts.set(badge, badge.innerHTML);
          }
          if (textToApply) badge.innerHTML = textToApply;
          badge.style.display = "";
        });
      });
    }

    function applySamePriceSaleStickyPreview() {
      if (!stickyBuyPrice) return;
      const preview = getSamePriceSalePreviewData(getVariantsList());
      if (!preview) return;

      const { minPrice, maxCompareAt } = preview;
      stickyBuyPrice.classList.add("price--on-sale");
      stickyBuyPrice.innerHTML =
        `<span class="price__regular whitespace-nowrap">${formatMoney(minPrice)}</span>` +
        `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(maxCompareAt)}</span>`;
    }

    function getSelectedVariant() {
      const checkedInputs = Array.from(
        variantPicker.querySelectorAll('input[type="radio"]:checked')
      );
      if (checkedInputs.length < totalOptions) return null;

      const selectedOptions = checkedInputs.map((inp) => inp.value);
      if (!productData.productVariants) return null;

      return (
        productData.productVariants.find(
          (v) =>
            v.options &&
            selectedOptions.every((opt, i) => v.options[i] === opt)
        ) || null
      );
    }

    // Après variant:change (thème) : aligner prix / badge sur la variante réellement sélectionnée
    function syncVariantSaleDisplay(variant) {
      if (!variant) return;
      if (tieredBundleActive) return;

      const onSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      const priceFmt = formatMoney(variant.price);

      container
        .querySelectorAll(".price[data-base-price]")
        .forEach((priceEl) => {
          if (
            priceEl.closest("[data-bundle-price]") ||
            priceEl.closest("product-recommendations")
          ) {
            return;
          }

          priceEl.dataset.basePrice = String(variant.price);
          priceEl.dataset.baseCompareAtPrice = onSale
            ? String(variant.compare_at_price)
            : "0";

          if (onSale) {
            const compareFmt = formatMoney(variant.compare_at_price);
            priceEl.classList.add("price--on-sale");
            priceEl.innerHTML =
              `<span class="price__regular whitespace-nowrap">${priceFmt}</span>` +
              `<span class="price__sale inline-flex items-center h-auto relative">${compareFmt}</span>`;
          } else {
            priceEl.classList.remove("price--on-sale");
            priceEl.innerHTML = `<span class="price__regular whitespace-nowrap">${priceFmt}</span>`;
          }
        });

      badgeContainers.forEach((badgesEl) => {
        badgesEl
          .querySelectorAll(".badge--onsale:not([data-cd-badge])")
          .forEach((badge) => {
            badge.style.display = onSale ? "" : "none";
          });
      });

      if (!onSale) {
        createdBadges.forEach((badge) => {
          if (badge.isConnected) badge.remove();
        });
        createdBadges.clear();
      }

      const titleWithPrice = container.querySelector(".product__title.with-price");
      if (titleWithPrice) {
        titleWithPrice.classList.toggle("with-price--on-sale", onSale);
      }

      if (stickyBuyPrice && stickyOptionsReverted) {
        if (onSale) {
          const compareFmt = formatMoney(variant.compare_at_price);
          stickyBuyPrice.classList.add("price--on-sale");
          stickyBuyPrice.innerHTML =
            `<span class="price__regular whitespace-nowrap">${priceFmt}</span>` +
            `<span class="price__sale inline-flex items-center h-auto relative">${compareFmt}</span>`;
        } else {
          stickyBuyPrice.classList.remove("price--on-sale");
          stickyBuyPrice.innerHTML = `<span class="price__regular whitespace-nowrap">${priceFmt}</span>`;
        }
      }
    }

    // Compute max savings across variants
    function getMaxSavings() {
      // Try to get variants from productData object first
      let variants = null;

      if (
        productData.productVariants &&
        Array.isArray(productData.productVariants)
      ) {
        variants = productData.productVariants;
        log(
          `[Variant Validation] Using productData.productVariants (${variants.length} variants)`
        );
      } else {
        // Fallback: Try to get variant data from variant-picker
        const variantPickerData = variantPicker.querySelector(
          "script[type='application/json']"
        );
        if (variantPickerData) {
          try {
            variants = JSON.parse(variantPickerData.textContent);
            log(
              `[Variant Validation] Parsed variant data from DOM (${variants.length} variants)`
            );
          } catch (e) {
            log("[Variant Validation] Failed to parse variant data:", e);
          }
        }
      }

      if (!variants || !Array.isArray(variants) || variants.length === 0) {
        log("[Variant Validation] No variant data available");
        return null;
      }

      let maxAmount = 0;
      let maxPercent = 0;

      variants.forEach((variant) => {
        const compareAt = variant.compare_at_price;
        const price = variant.price;
        if (compareAt && price && compareAt > price) {
          const diff = compareAt - price;
          const percent = Math.round((diff / compareAt) * 100);
          if (diff > maxAmount) maxAmount = diff;
          if (percent > maxPercent) maxPercent = percent;
        }
      });

      if (maxAmount <= 0 && maxPercent <= 0) {
        log("[Variant Validation] No savings found in variants");
        return null;
      }

      log(
        `[Variant Validation] Max savings: amount=${maxAmount}, percent=${maxPercent}%`
      );

      const formatMoney = (amount) => {
        if (typeof theme !== "undefined" && theme.Currency) {
          return theme.Currency.formatMoney(
            amount,
            theme.settings.currencyCodeEnabled
              ? theme.settings.moneyWithCurrencyFormat
              : theme.settings.moneyFormat
          );
        }
        return `€${(amount / 100).toFixed(2)}`;
      };

      return {
        amount: maxAmount,
        amountFormatted: maxAmount > 0 ? formatMoney(maxAmount) : "",
        percent: maxPercent > 0 ? maxPercent : 0,
      };
    }

    // Applique le badge « jusqu'à » quand les prix finaux varient + au moins une promo.
    // Dans les autres cas (prix identiques + promo, ou pas de promo) : le badge natif
    // Liquid est conservé tel quel — c'est le comportement STOYIK Concept SAVE.
    function applyBadgeOverride() {
      log(
        `[Variant Validation] applyBadgeOverride called, ${badgeContainers.length} badge container(s)`
      );

      if (badgeContainers.length === 0) {
        log(
          "[Variant Validation] No badge containers found, skipping badge override"
        );
        return;
      }

      const variants = getVariantsList();

      if (!shouldUseUpToRange(variants)) {
        // Prix finaux identiques ou pas de promo → badge natif (prix barré Shopify) ou absent.
        // Aucune modification : on laisse le rendu Liquid tel quel, comme dans STOYIK Concept SAVE.
        log(
          "[Variant Validation] Prix finaux identiques ou sans promo — badge natif conservé"
        );
        // Nettoyer d'éventuels badges créés par un appel précédent (cas défensif)
        createdBadges.forEach((badge) => badge.remove());
        createdBadges.clear();
        originalBadgeTexts.forEach((text, badge) => {
          if (badge.isConnected) badge.innerHTML = text;
        });
        originalBadgeTexts.clear();
        return;
      }

      const savings = getMaxSavings();
      log("[Variant Validation] Max savings:", savings);

      if (!savings) {
        log("[Variant Validation] No savings found, skipping badge override");
        return;
      }

      badgeContainers.forEach((container, index) => {
        const { saveType, upToTemplate, saleLabel } = getBadgeMeta(container);
        log(
          `[Variant Validation] Badge container ${
            index + 1
          }: saveType=${saveType}, upToTemplate=${upToTemplate}, saleLabel=${saleLabel}`
        );

        let badges = Array.from(container.querySelectorAll(".badge--onsale"));
        log(
          `[Variant Validation] Found ${
            badges.length
          } existing badge(s) in container ${index + 1}`
        );

        // Create badge if none exists
        if (badges.length === 0) {
          log(
            `[Variant Validation] Creating new badge in container ${index + 1}`
          );
          const newBadge = document.createElement("span");
          newBadge.className =
            "badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full";
          newBadge.dataset.variantValidationBadge = "true";
          container.appendChild(newBadge);
          badges = [newBadge];
          createdBadges.add(newBadge);
        }

        let textToApply = "";
        if (saveType === "label") {
          textToApply = saleLabel || "";
        } else if (saveType === "dollar") {
          if (!savings.amountFormatted) return;
          if (upToTemplate) {
            textToApply = upToTemplate
              .replace("__amount__", savings.amountFormatted)
              .replace("{{ saved_amount }}", savings.amountFormatted);
          } else {
            textToApply = savings.amountFormatted;
          }
        } else {
          if (!savings.percent) return;
          const percentText = `${savings.percent}%`;
          if (upToTemplate) {
            textToApply = upToTemplate
              .replace("__amount__", percentText)
              .replace("{{ saved_amount }}", percentText);
          } else {
            textToApply = percentText;
          }
        }

        log(
          `[Variant Validation] Badge text to apply in container ${
            index + 1
          }: "${textToApply}"`
        );

        badges.forEach((badge) => {
          if (!originalBadgeTexts.has(badge)) {
            originalBadgeTexts.set(badge, badge.innerHTML);
          }
          if (textToApply) {
            badge.innerHTML = textToApply;
            log(`[Variant Validation] Applied badge text: "${textToApply}"`);
          }
          badge.style.display = "";
        });
      });
    }

    // Restore badge text and remove created badges
    function restoreBadgeOverride() {
      createdBadges.forEach((badge) => {
        badge.remove();
      });
      createdBadges.clear();

      originalBadgeTexts.forEach((text, badge) => {
        badge.innerHTML = text;
      });
      originalBadgeTexts.clear();
    }

    // Function to get from price text
    function getFromPriceText(price) {
      // Get translation from theme.strings
      let translation = "From €{{ price }}";
      if (
        typeof theme !== "undefined" &&
        theme.strings &&
        theme.strings.fromPriceMin
      ) {
        translation = theme.strings.fromPriceMin;
      }

      // Format price
      let formattedPrice = "";
      if (typeof theme !== "undefined" && theme.Currency) {
        formattedPrice = theme.Currency.formatMoney(
          price,
          theme.settings.currencyCodeEnabled
            ? theme.settings.moneyWithCurrencyFormat
            : theme.settings.moneyFormat
        );
      } else {
        // Fallback: simple formatting
        formattedPrice = `€${(price / 100).toFixed(2)}`;
      }

      // Replace {{ price }} placeholder
      return translation.replace("{{ price }}", formattedPrice);
    }

    // Function to replace price with "From €..." if prices vary
    function replacePriceWithFromPrice() {
      log("[Variant Validation] replacePriceWithFromPrice called");

      // First try to get price data from productData object
      let priceVaries = false;
      let priceMin = null;

      if (
        productData.productPriceVaries !== undefined &&
        productData.productPriceMin !== undefined
      ) {
        priceVaries = productData.productPriceVaries;
        priceMin = productData.productPriceMin;
        log(
          `[Variant Validation] Using productData price data: varies=${priceVaries}, min=${priceMin}`
        );
      } else {
        // Fallback: Try to get variant data from variant-picker data
        const variantPickerData = variantPicker.querySelector(
          "script[type='application/json']"
        );
        if (variantPickerData) {
          try {
            const variantsData = JSON.parse(variantPickerData.textContent);
            if (Array.isArray(variantsData) && variantsData.length > 0) {
              const prices = variantsData.map((v) => v.price);
              priceMin = Math.min(...prices);
              const priceMax = Math.max(...prices);
              priceVaries = priceMin !== priceMax;
              log(
                `[Variant Validation] Computed from variant data: varies=${priceVaries}, min=${priceMin}, max=${priceMax}`
              );
            }
          } catch (e) {
            log("[Variant Validation] Failed to parse variant data:", e);
          }
        }
      }

      // If prices don't vary or we couldn't get price data, don't modify
      if (!priceVaries || !priceMin) {
        log(
          `[Variant Validation] Not replacing price: varies=${priceVaries}, min=${priceMin}`
        );
        return;
      }

      const fromPriceText = getFromPriceText(priceMin);
      log(`[Variant Validation] From price text: "${fromPriceText}"`);

      priceRegularElements.forEach((priceEl, index) => {
        // Store original if not already stored
        if (!originalPriceTexts.has(priceEl)) {
          originalPriceTexts.set(priceEl, priceEl.innerHTML);
        }
        priceEl.innerHTML = fromPriceText;
        log(`[Variant Validation] Replaced price in element ${index + 1}`);
      });

      // Hide struck-through sale prices so they don't show alongside "Dès ..." text
      priceSaleElements.forEach((saleEl) => {
        if (!originalPriceSaleDisplays.has(saleEl)) {
          originalPriceSaleDisplays.set(saleEl, saleEl.style.display);
        }
        saleEl.style.display = "none";
      });
    }

    // Function to restore original prices
    function restoreOriginalPrices() {
      // Ne pas réinjecter l'aperçu promo : le thème met à jour Price via variant:change
      originalPriceContainerHTML.clear();
      originalPriceTexts.clear();

      priceSaleElements.forEach((saleEl) => {
        const originalDisplay = originalPriceSaleDisplays.get(saleEl);
        saleEl.style.display =
          originalDisplay !== undefined ? originalDisplay : "";
      });
      originalPriceSaleDisplays.clear();
    }

    // Function to add red color to metafield/translation text
    function addRedColorToText() {
      if (labelErrorTimeout) {
        clearTimeout(labelErrorTimeout);
        labelErrorTimeout = null;
      }
      const currentDivs = getLabelTextDivs();
      const textToUse = getVariantSelectText();

      // Store references to divs that we color red
      const coloredDivs = [];

      // Get the computed color value from CSS variable
      const errorColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--color-error-text")
        .trim();

      currentDivs.forEach((div) => {
        // Check if the div contains the metafield/translation text
        const currentText = div.textContent.trim();
        const divHTML = div.innerHTML.trim();

        // Apply red color if the div shows the metafield/translation text
        // We check both text content and HTML content to be safe
        const matchesText =
          currentText === textToUse ||
          divHTML === textToUse ||
          currentText.includes(textToUse) ||
          divHTML.includes(textToUse);

        if (matchesText) {
          // Apply red color using the CSS variable value
          if (errorColor) {
            div.style.color = `rgb(${errorColor})`;
          } else {
            // Fallback if variable not found
            div.style.color = "rgb(var(--color-error-text))";
          }
          coloredDivs.push(div);
        }
      });

      // If no divs were found, try a more direct approach
      if (coloredDivs.length === 0) {
        // Try to find divs by text content directly
        const allLabelDivs = Array.from(
          variantPicker.querySelectorAll(".form__label > .flex.gap-2")
        );
        allLabelDivs.forEach((div) => {
          const currentText = div.textContent.trim();
          if (currentText === textToUse || currentText.includes(textToUse)) {
            if (errorColor) {
              div.style.color = `rgb(${errorColor})`;
            } else {
              div.style.color = "rgb(var(--color-error-text))";
            }
            coloredDivs.push(div);
          }
        });
      }

      // Remove after 3 seconds (reset if re-triggered)
      labelErrorTimeout = setTimeout(() => {
        coloredDivs.forEach((div) => {
          // Re-check if it still contains the metafield text before removing color
          const currentText = div.textContent.trim();
          const divHTML = div.innerHTML.trim();
          const stillMatchesText =
            currentText === textToUse ||
            divHTML === textToUse ||
            currentText.includes(textToUse) ||
            divHTML.includes(textToUse);

          if (stillMatchesText) {
            div.style.color = "";
          }
        });
        labelErrorTimeout = null;
      }, 3000);
    }

    // Function to clear red color from metafield/translation text
    function clearRedColorFromText() {
      if (labelErrorTimeout) {
        clearTimeout(labelErrorTimeout);
        labelErrorTimeout = null;
      }
      const currentDivs = getLabelTextDivs();
      currentDivs.forEach((div) => {
        div.style.color = "";
      });
    }

    // Listen to variant changes to update original texts
    variantPicker.addEventListener("change", function () {
      // Re-select divs in case DOM was updated
      const currentDivs = getLabelTextDivs();

      // Update the stored original texts for current divs
      currentDivs.forEach((div) => {
        if (div && div.innerHTML) {
          // Only update if it's not the metafield/translation text
          const textToUse = getVariantSelectText();
          const currentText = div.textContent.trim();
          if (currentText !== textToUse) {
            originalTexts.set(div, div.innerHTML);
          }
        }
      });

      // If all variants are clicked, restore the texts (which are now updated)
      if (allVariantsClicked()) {
        currentDivs.forEach((div) => {
          const originalText = originalTexts.get(div);
          if (originalText) {
            div.innerHTML = originalText;
          }
        });
      } else {
        // If not all variants are clicked, keep showing metafield/translation text
        currentDivs.forEach((div) => {
          const fieldset = div.closest("fieldset.product-form__input");
          const meta = findMetaForFieldset(fieldset);
          const textToUse =
            (meta &&
              typeof meta.selection === "string" &&
              meta.selection.trim() !== "" &&
              meta.selection) ||
            getVariantSelectText();
          div.innerHTML = textToUse;
        });
      }
    });

    // Track clicks on variant inputs and labels
    // IMPORTANT: We only track variants that the user explicitly clicks,
    // NOT variants that are pre-selected on page load
    variantInputs.forEach((input) => {
      // Function to handle variant click/change
      const handleVariantInteraction = function (inputElement) {
        // Skip if validation is already complete
        if (validationComplete) {
          return;
        }

        // Track this variant as clicked by the user
        clickedVariants.add(inputElement.id);

        // On first variant interaction: restore default disabled behavior.
        // We removed disabled on page load (step 7 above); now the theme takes over.
        // For an actual variant change, the theme's change handler will update disabled.
        // For a re-click on the same variant (no change event), we restore it manually.
        if (!userHasInteracted) {
          userHasInteracted = true;
          container.setAttribute("data-user-has-interacted", "true");
          const checkedInputs = Array.from(
            variantPicker.querySelectorAll('input[type="radio"]:checked')
          );
          const selectedOptions = checkedInputs.map((inp) => inp.value);
          if (productData.productVariants && selectedOptions.length > 0) {
            const currentVariant = productData.productVariants.find(
              (v) =>
                v.options &&
                selectedOptions.every((opt, i) => v.options[i] === opt)
            );
            if (currentVariant) {
              addToCartButtons.forEach((btn) => {
                if (!currentVariant.available) {
                  btn.setAttribute("disabled", "");
                } else {
                  btn.removeAttribute("disabled");
                }
              });
            }
          }
          // Re-apply styles now that userHasInteracted is true, so the swatch border
          // switches back to --color-border regardless of the initial sold-out state.
          applyModifiedStyles();
        }

        // On first click: revert sticky options text and price
        if (!stickyOptionsReverted && (stickyProductOptions || stickyBuyPrice)) {
          stickyOptionsReverted = true;

          if (stickyProductOptions) {
            // Resolve actual current variant title from checked radio inputs
            const checkedInputs = Array.from(variantPicker.querySelectorAll('input[type="radio"]:checked'));
            const selectedOptions = checkedInputs.map((inp) => inp.value);
            let currentVariantTitle = null;
            if (productData.productVariants && selectedOptions.length > 0) {
              const variant = productData.productVariants.find(
                (v) => v.options && selectedOptions.every((opt, i) => v.options[i] === opt)
              );
              if (variant) currentVariantTitle = variant.title;
            }
            stickyProductOptions.innerText = currentVariantTitle || stickyOriginalText;
          }

          if (stickyBuyPrice) {
            const checkedBundles = Array.from(
              (sectionEl || container).querySelectorAll('input[name="bundles"]:checked')
            );
            if (checkedBundles.length > 0) {
              // Bundles checked: recalculate total (variant price + bundles).
              productForm.dispatchEvent(new CustomEvent('bundle:change', {
                detail: {
                  items: checkedBundles.map(function (input) {
                    return {
                      id: input.value,
                      quantity: 1,
                      price: input.getAttribute('data-price')
                    };
                  })
                }
              }));
            } else if (typeof window.__syncStickyBuyPriceFromAtc === 'function') {
              window.__syncStickyBuyPriceFromAtc(productForm);
            } else if (!tieredBundleActive) {
              // Tiered bundle : tiered-bundle.js gère le rendu TB du sticky.
              var nativeHTML = stickyBuyPrice._originalHTML !== undefined
                ? stickyBuyPrice._originalHTML
                : stickyOriginalPriceHTML;
              if (nativeHTML !== null && nativeHTML !== undefined) {
                stickyBuyPrice.innerHTML = nativeHTML;
              }
            }
          }
        }

        // Clear error highlight immediately when user interacts
        removeErrorStyle();

        // Check if all variants are now clicked
        if (allVariantsClicked()) {
          markVariantSelectionComplete();

          // Restore default styles immediately
          removeModifiedStyles();

          // Restore all other elements
          restoreLabelTexts();
          restoreButtonTexts();
          showPriceElements();
          if (!tieredBundleActive) {
            restoreOriginalPrices();
            restorePriceRegularColor();
            if (!productKeepNativeSalePrice) {
              restoreBadgeOverride();
            } else {
              createdBadges.forEach((badge) => badge.remove());
              createdBadges.clear();
              originalBadgeTexts.clear();
            }

            const selectedVariant = getSelectedVariant();
            if (selectedVariant) {
              syncVariantSaleDisplay(selectedVariant);
            }
          }
        }
      };

      // Listen to click events to track when user clicks on a variant
      // This works even if the variant is already selected
      input.addEventListener("click", function () {
        handleVariantInteraction(this);
      });

      // Also listen to change events as a backup
      input.addEventListener("change", function () {
        handleVariantInteraction(this);
      });

      // Also listen to clicks on the associated label
      // This ensures clicks on labels also count
      const label = variantPicker.querySelector(`label[for="${input.id}"]`);
      if (label) {
        label.addEventListener("click", function () {
          // Execute immediately - the change event will handle the rest
          handleVariantInteraction(input);
        });
      }
    });

    // Apply visual modifications immediately in order of importance
    // This ensures fastest perceived performance

    const initVariants = getVariantsList();
    productKeepNativeSalePrice = hasSamePriceVariantSale(initVariants);
    productUseUpToRange = shouldUseUpToRange(initVariants);

    // 1. Apply styles first (most critical for visual consistency)
    applyModifiedStyles();

    // 2. Prix / badge avant sélection de variante (tiered-bundle.js gère TB)
    if (!tieredBundleActive) {
      if (productUseUpToRange) {
        removePriceRegularColor();
        replacePriceWithFromPrice();
        applyBadgeOverride();
      } else if (productKeepNativeSalePrice) {
        // Même prix final, une variante en promo : badge + prix barré (pas de « jusqu'à »)
        applySamePriceSalePricePreview();
        applySamePriceSaleBadge();
      }
    }

    // 3. Texte bouton = « Choisir une variante » uniquement (prix masqué jusqu'à sélection)
    replaceButtonText();
    if (labelTextDivs.length > 0) {
      replaceLabelTexts();
    }

    // 4. Masquer le prix dans le bouton tant qu'aucune variante n'a été sélectionnée
    hidePriceElements();

    // 5. Sticky : texte variante + aperçu prix avant premier clic
    if (stickyProductOptions) {
      const stickySelectText = getVariantSelectText();
      if (stickySelectText) {
        stickyProductOptions.innerText = stickySelectText;
      }
    }
    if (stickyBuyPrice && !tieredBundleActive) {
      if (
        productUseUpToRange &&
        productData.productPriceVaries &&
        productData.productPriceMin
      ) {
        stickyBuyPrice.innerHTML = getFromPriceText(productData.productPriceMin);
      } else if (productKeepNativeSalePrice) {
        applySamePriceSaleStickyPreview();
      }
    }

    // Guard: bundle:change peut écraser l'aperçu sticky avant le premier clic variante
    if (
      !tieredBundleActive &&
      stickyBuyPrice &&
      (productUseUpToRange || productKeepNativeSalePrice)
    ) {
      productForm.addEventListener("bundle:change", function () {
        if (!stickyOptionsReverted) {
          setTimeout(function () {
            if (!stickyOptionsReverted) {
              if (
                productUseUpToRange &&
                productData.productPriceVaries &&
                productData.productPriceMin
              ) {
                stickyBuyPrice.innerHTML = getFromPriceText(
                  productData.productPriceMin
                );
              } else if (productKeepNativeSalePrice) {
                applySamePriceSaleStickyPreview();
              }
            }
          }, 0);
        }
      });
    }

    // Après mise à jour AJAX du thème : prix / badge selon la variante sélectionnée
    productForm.addEventListener("variant:change", function (e) {
      const variant = e.detail?.variant || getSelectedVariant();
      if (!variant) return;

      requestAnimationFrame(function () {
        if (!tieredBundleActive) {
          if (!validationComplete) {
            if (productKeepNativeSalePrice && !allVariantsClicked()) {
              applySamePriceSalePricePreview();
              applySamePriceSaleBadge();
              return;
            }
            if (productUseUpToRange && !allVariantsClicked()) {
              return;
            }
          }
          syncVariantSaleDisplay(variant);
        } else {
          if (typeof window.__syncStickyBuyPriceFromAtc === "function") {
            window.__syncStickyBuyPriceFromAtc(productForm);
          }
          if (validationComplete || allVariantsClicked()) {
            if (!validationComplete) markVariantSelectionComplete();
          }
        }
      });
    });

    // 7. Remove disabled from add-to-cart buttons on page load.
    // If the pre-selected variant is sold out, the server renders the button as disabled.
    // We lift that restriction so the button is always clickable before the first variant interaction.
    // After the first interaction, the theme's own change handler restores the correct disabled state.
    addToCartButtons.forEach((btn) => btn.removeAttribute("disabled"));

    // Intercept form submission
    productForm.addEventListener(
      "submit",
      function (e) {
        // If validation is complete, allow submission
        if (validationComplete) {
          return true;
        }

        // Check if all variants have been clicked
        if (!allVariantsClicked()) {
          e.preventDefault();
          e.stopPropagation();

          // Add error style
          addErrorStyle();

          // Add red color to metafield/translation text
          addRedColorToText();

          // Scroll to variant picker
          scrollToVariantPicker();

          return false;
        } else {
          markVariantSelectionComplete();
        }
      },
      true
    ); // Use capture phase to intercept before default handler
  }
})();
